import { useState } from 'react';
import { submitContactForm } from '../api/contact';
import EditableText from './EditableText';
import { useContent } from '../context/ContentContext';
import BottomBar from './BottomBar';

const Contact = ({ onOpenTerms }) => {
  const { getText } = useContent();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    smsOptIn: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await submitContactForm({
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        message: formData.message || '(No message provided)',
        phone: formData.phone || undefined,
        subject: 'Contact form submission',
        SMSOPTIN: formData.smsOptIn ? 'yes' : undefined,
      });

      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        smsOptIn: false
      });

      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-8 md:py-10 lg:py-12 pb-16 md:pb-20 lg:pb-24 px-4 sm:px-6 lg:px-8 m-0 bg-[#fafafa] relative overflow-visible w-full max-w-[100vw]"
      style={{
        backgroundImage: 'radial-gradient(circle, rgba(0, 0, 0, 0.03) 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }}
    >
      <div className="!max-w-[1188px] mx-auto w-full relative z-[1]">
        {/* Section Title */}
        <h2 className="text-center text-2xl md:text-3xl lg:text-4xl mb-6 md:mb-8 text-white relative block w-full font-semibold uppercase tracking-[4px] font-serif border-b-0 pb-4 after:content-[''] after:absolute after:-bottom-[8px] after:left-1/2 after:-translate-x-1/2 after:w-[150px] sm:after:w-[200px] md:after:w-[250px] after:h-[3px] sm:after:h-[4px] md:after:h-[5px] after:bg-[#9a83ad]">
          <EditableText path="contact.sectionTitle" fallback="Contact Us" />
        </h2>

        {/* Contact Container - Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 p-0 md:p-3 relative z-[1] items-stretch">
          {/* Contact Info Section - order-2 on mobile so Send us a Message appears first */}
          <div className="order-2 md:order-1 p-6 md:p-8 bg-[var(--bg-dark)] border-2 border-[#9a83ad] text-white flex flex-col h-full shadow-[0_4px_6px_rgba(0,0,0,0.07),0_2px_4px_rgba(0,0,0,0.05)]">
            <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-[2px] mb-6 text-white/90 border-b-[3px] border-double border-white/50 pb-3 font-serif">
              <EditableText path="contact.contactCardTitle" fallback="Contact Us" />
            </h2>
            
            <div className="company-details">
              <h3 className="text-lg md:text-xl font-bold uppercase tracking-[1px] mb-6 text-white/85 font-serif">
                <EditableText path="contact.companyName" fallback="The VBC Group" />
              </h3>
              
              <div className="mb-6">
                <strong className="block text-base uppercase tracking-[1px] mb-1.5 text-white/80 font-bold">
                  Address:
                </strong>
                <p className="text-white/70 leading-[1.6] text-sm md:text-base m-0">
                  {(getText('contact.address') ?? '1527 South Cooper Street\nArlington, TX 76010').split('\n').map((line, i) => (
                    <span key={i}>{line}{i < 1 ? <br /> : null}</span>
                  ))}
                </p>
              </div>
              
              <div className="mb-6">
                <strong className="block text-base uppercase tracking-[1px] mb-1.5 text-white/80 font-bold">
                  Email:
                </strong>
                <p className="text-white/70 leading-[1.6] text-sm md:text-base m-0">
                  <a 
                    href={`mailto:${getText('contact.email') ?? 'info@thevbcgroup.com'}`}
                    className="text-white/70 no-underline transition-opacity duration-300 hover:text-white/90 hover:underline"
                  >
                    <EditableText path="contact.email" fallback="info@thevbcgroup.com" />
                  </a>
                </p>
              </div>
              
              <div className="mb-0">
                <strong className="block text-base uppercase tracking-[1px] mb-1.5 text-white/80 font-bold">
                  Phone:
                </strong>
                <p className="text-white/70 leading-[1.6] text-sm md:text-base m-0">
                  <a 
                    href={`tel:${(getText('contact.phone') ?? '(800) 231-5629').replace(/\D/g, '')}`}
                    className="text-white/70 no-underline transition-opacity duration-300 hover:text-white/90 hover:underline"
                  >
                    <EditableText path="contact.phone" fallback="(800) 231-5629" />
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form Section - order-1 on mobile so it appears first */}
          <div className="order-1 md:order-2 p-6 md:p-8 bg-[#5a5a5a] border border-[var(--border-light)] flex flex-col h-full shadow-[0_4px_6px_rgba(0,0,0,0.07),0_2px_4px_rgba(0,0,0,0.05)]">
            <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-[2px] mb-6 text-white border-b border-white/20 pb-4 font-serif">
              <EditableText path="contact.sendMessageTitle" fallback="Send us a Message" />
            </h2>
            
            <form onSubmit={handleSubmit} className="flex flex-col gap-2.5 flex-1">
              {/* Form Row - First Name and Last Name */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex flex-col">
                  <label 
                    htmlFor="firstName" 
                    className="text-xs font-semibold uppercase tracking-[1.5px] mb-1.5 text-white font-serif"
                  >
                    <EditableText path="contact.firstName" fallback="First Name" />
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="py-1.5 px-3 border border-white/30 rounded-none text-sm font-serif bg-white text-[var(--text-dark)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus:outline-none focus:border-[#9a83ad] focus:bg-white focus:shadow-[0_0_0_3px_rgba(154,131,173,0.12)]"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label 
                    htmlFor="lastName" 
                    className="text-xs font-semibold uppercase tracking-[1.5px] mb-1.5 text-white font-serif"
                  >
                    <EditableText path="contact.lastName" fallback="Last Name" />
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="py-1.5 px-3 border border-white/30 rounded-none text-sm font-serif bg-white text-[var(--text-dark)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus:outline-none focus:border-[#9a83ad] focus:bg-white focus:shadow-[0_0_0_3px_rgba(154,131,173,0.12)]"
                  />
                </div>
              </div>

              {/* Email Field */}
              <div className="flex flex-col">
                <label 
                  htmlFor="email" 
                  className="text-xs font-semibold uppercase tracking-[1.5px] mb-1.5 text-white font-serif"
                >
                  <EditableText path="contact.emailLabel" fallback="Email" />
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="py-1.5 px-3 border border-white/30 rounded-none text-sm font-serif bg-white text-[var(--text-dark)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus:outline-none focus:border-[#9a83ad] focus:bg-white focus:shadow-[0_0_0_3px_rgba(154,131,173,0.12)]"
                />
              </div>

              {/* Phone Field */}
              <div className="flex flex-col">
                <label 
                  htmlFor="phone" 
                  className="text-xs font-semibold uppercase tracking-[1.5px] mb-1.5 text-white font-serif"
                >
                  <EditableText path="contact.phoneLabel" fallback="Phone" />
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="py-1.5 px-3 border border-white/30 rounded-none text-sm font-serif bg-white text-[var(--text-dark)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus:outline-none focus:border-[#9a83ad] focus:bg-white focus:shadow-[0_0_0_3px_rgba(154,131,173,0.12)]"
                />
              </div>

              {/* Message Field */}
              <div className="flex flex-col">
                <label 
                  htmlFor="message" 
                  className="text-xs font-semibold uppercase tracking-[1.5px] mb-1.5 text-white font-serif"
                >
                  <EditableText path="contact.messageLabel" fallback="Message" /> <span className="normal-case">(optional)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="py-1.5 px-3 border border-white/30 rounded-none text-sm font-serif bg-white text-[var(--text-dark)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] resize-y min-h-[60px] focus:outline-none focus:border-[#9a83ad] focus:bg-white focus:shadow-[0_0_0_3px_rgba(154,131,173,0.12)]"
                />
              </div>

              {/* SMS Opt-In Checkbox */}
              <div className="mt-1">
                <label className="flex items-start gap-2 cursor-pointer text-[10px] leading-[1.4] text-white/90">
                  <input
                    type="checkbox"
                    id="smsOptIn"
                    name="smsOptIn"
                    checked={formData.smsOptIn}
                    onChange={handleChange}
                    className="w-[16px] h-[16px] mt-0.5 flex-shrink-0 cursor-pointer accent-[#9a83ad]"
                  />
                  <span className="flex-1 text-[10px] leading-[1.4] text-white/90">
                    <strong className="text-white font-semibold">SMS Opt-In</strong> In checking the SMS Opt-In box, you are giving your consent to receive polling, impact notifications and updates via SMS/MMS messages to the provided phone number from The VBC Group. Donations will not be solicited. Msg frequency will vary. Msg & data rates may apply. Text HELP for assistance. Reply STOP to opt out and stop receiving future texts. Mobile opt-in data will not be shared with 3rd parties. Privacy Policy is found below.
                  </span>
                </label>
                <p className="mt-2 ml-[24px] text-[0.65rem] leading-[1.3] text-white/80">
                  <strong className="text-white font-semibold">PRIVACY POLICY:</strong> We will not share your opt-in to an SMS campaign with any third party.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`py-3 px-12 bg-[var(--text-dark)] text-white border border-[var(--text-dark)] rounded-none text-base md:text-lg font-semibold uppercase tracking-[2px] cursor-pointer transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] font-serif self-start relative overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(0,0,0,0.1),0_5px_10px_rgba(0,0,0,0.04)] before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-[#9a83ad] before:transition-[left] before:duration-[500ms] before:ease-[cubic-bezier(0.4,0,0.2,1)] before:-z-10 hover:before:left-0 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Sending...' : <EditableText path="contact.submitButton" fallback="Send Message" tag="span" />}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-500/20 border border-green-500 text-green-200 text-sm">
                  <EditableText path="contact.successMessage" fallback="Message sent successfully! We'll get back to you soon." />
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-500/20 border border-red-500 text-red-200 text-sm">
                  <EditableText path="contact.errorMessage" fallback="Failed to send message. Please try again or contact us directly at info@thevbcgroup.com" />
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Footer appended to contact so section background scrolls with it; footer has solid dark background for readability */}
      <BottomBar onOpenTerms={onOpenTerms} />
    </section>
  );
};

export default Contact;

